import { Compass, Lock, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	browsePublicProjects,
	fetchSavedIdeas,
	fetchToggleSavedIdea,
	getSavedIdeas,
	type PublicProjectSummary,
} from "@/features/marketplace/services/marketplace-api.service";

/** How long the typing must stop before the page searches. */
export const SEARCH_DEBOUNCE_MS = 300;

export function ExplorePublicPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const searchQuery = searchParams.get("search")?.trim() ?? "";
	const { isAuthenticated, getUserType } = useAuthStore();
	const [projects, setProjects] = useState<PublicProjectSummary[]>([]);
	const [total, setTotal] = useState(0);
	const [savedIdeas, setSavedIdeas] = useState<string[]>([]);
	const [toggleInFlight, setToggleInFlight] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [loginPrompt, setLoginPrompt] = useState<string | null>(null);
	// What is typed in the box; `searchQuery` is what the page last searched.
	const [draft, setDraft] = useState(searchQuery);
	// The last search this page put in the address. Another value there came
	// from outside, for example from Back, so the box must show it.
	const writtenQueryRef = useRef(searchQuery);
	// One save toggle at a time. The ref stops a second click at once, before
	// the state change shows the disabled buttons.
	const toggleInFlightRef = useRef(false);

	useEffect(() => {
		if (searchQuery === writtenQueryRef.current) return;
		writtenQueryRef.current = searchQuery;
		setDraft(searchQuery);
	}, [searchQuery]);

	// The ?search= param is what the home search bars link to; the server
	// matches it against every public project, not just the page shown.
	const visibleProjects = projects;

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			setIsLoading(true);
			try {
				const publicProjects = await browsePublicProjects({
					limit: 40,
					q: searchQuery || undefined,
				});
				if (cancelled) return;
				setProjects(publicProjects.projects);
				setTotal(publicProjects.total);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [searchQuery]);

	useEffect(() => {
		const term = draft.trim();
		if (term === searchQuery) return;
		const timer = setTimeout(() => {
			writtenQueryRef.current = term;
			setSearchParams(term ? { search: term } : {}, { replace: true });
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [draft, searchQuery, setSearchParams]);

	function clearSearch() {
		setDraft("");
		writtenQueryRef.current = "";
		setSearchParams({}, { replace: true });
	}

	useEffect(() => {
		if (!isAuthenticated() || getUserType() !== "homeowner") return;
		let cancelled = false;
		getSavedIdeas().then((savedIdeasData) => {
			if (!cancelled) setSavedIdeas(savedIdeasData.projectIds);
		});
		return () => {
			cancelled = true;
		};
	}, [getUserType, isAuthenticated]);

	async function onToggleSaveIdea(projectId: string) {
		if (!isAuthenticated()) {
			setLoginPrompt("Please log in to save project ideas.");
			return;
		}

		if (getUserType() !== "homeowner") {
			setLoginPrompt("Only homeowners can save project ideas.");
			return;
		}

		if (toggleInFlightRef.current) return;
		toggleInFlightRef.current = true;
		setToggleInFlight(true);
		try {
			const result = await fetchToggleSavedIdea(projectId);
			setSavedIdeas(result.projectIds);
		} catch (error) {
			console.error("Failed to toggle saved idea:", error);
			toast.error(t("inspire.saveFailed"));
			// The toggle can fail after the server changed the list, for example
			// when the answer is lost. Read the list again to show what the server
			// has. If that read also fails, keep what the page shows.
			try {
				setSavedIdeas((await fetchSavedIdeas()).projectIds);
			} catch (reloadError) {
				console.error("Failed to reload saved ideas:", reloadError);
			}
		} finally {
			toggleInFlightRef.current = false;
			setToggleInFlight(false);
		}
	}

	return (
		<div className="mx-auto min-h-screen w-full max-w-5xl space-y-8 p-4 sm:p-6">
			<div className="rounded-xl bg-linear-to-r from-primary/15 via-accent/20 to-primary/5 p-6">
				<div className="flex items-center gap-2 text-primary">
					<Compass className="h-4 w-4" />
					<p className="text-sm font-medium">
						{t("dashboard.explore", "Explore")}
					</p>
				</div>
				<h1 className="mt-2 text-2xl font-bold text-foreground">
					Trending Transformations
				</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					{isAuthenticated()
						? "Browse public before/after transformations."
						: "Browse public transformations. Sign in to save ideas and start your own project."}
				</p>
				{!isAuthenticated() && (
					<div className="mt-4 flex flex-wrap gap-2">
						<Button onClick={() => navigate("/login")}>Sign up / Log in</Button>
					</div>
				)}
			</div>

			{loginPrompt && !isAuthenticated() && (
				<Card className="border-primary/40">
					<CardContent className="flex items-start gap-3 p-4">
						<Lock className="mt-0.5 h-4 w-4 text-primary" />
						<div className="space-y-2">
							<p className="text-sm text-foreground">{loginPrompt}</p>
							<Button size="sm" onClick={() => navigate("/login")}>
								Sign up / Log in
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			<section>
				<h2 className="mb-4 text-lg font-semibold text-foreground">
					Trending Transformations
				</h2>
				<div className="relative mb-4">
					<Search
						className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
						aria-hidden="true"
					/>
					<Input
						type="search"
						className="pl-9"
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						aria-label={t("explorePublic.searchLabel")}
						placeholder={t("homeSearch.placeholder")}
					/>
				</div>
				{isLoading ? (
					<div className="grid gap-4 md:grid-cols-2">
						{["a", "b", "c", "d"].map((key) => (
							<Skeleton key={key} className="h-52 rounded-lg" />
						))}
					</div>
				) : (
					<>
						{searchQuery && (
							<div className="mb-4 flex items-center gap-3">
								<p className="text-sm text-muted-foreground">
									{visibleProjects.length === 0
										? t("explorePublic.noMatch", { query: searchQuery })
										: t("explorePublic.showing", {
												shown: visibleProjects.length,
												total,
												query: searchQuery,
											})}
								</p>
								<Button variant="outline" size="sm" onClick={clearSearch}>
									Clear search
								</Button>
							</div>
						)}
						<div className="grid gap-4 md:grid-cols-2">
							{visibleProjects.map((project) => (
								<div
									key={project.id}
									className="rounded-lg border bg-card p-4 text-left shadow-sm"
								>
									<button
										type="button"
										onClick={() => navigate(`/public-projects/${project.id}`)}
										className="w-full text-left transition-shadow hover:opacity-90"
									>
										<div className="grid gap-2 sm:grid-cols-2">
											{project.previewImages?.[0] ? (
												<img
													src={project.previewImages[0]}
													alt={`${project.title} before`}
													className="h-28 w-full rounded-md object-cover"
												/>
											) : (
												<div className="rounded-md bg-muted p-6 text-center text-xs font-medium text-muted-foreground">
													Before
												</div>
											)}
											{project.previewImages?.[1] ? (
												<img
													src={project.previewImages[1]}
													alt={`${project.title} after`}
													className="h-28 w-full rounded-md object-cover"
												/>
											) : (
												<div className="rounded-md bg-primary/10 p-6 text-center text-xs font-medium text-primary">
													After
												</div>
											)}
										</div>
										<p className="mt-3 font-semibold text-foreground">
											{project.title}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											{project.scope === "full_home" ? "Home" : "Partial"} •{" "}
											{project.homeownerName}
										</p>
										{project.previewImages.length > 2 && (
											<p className="mt-1 text-xs text-primary">
												+{Math.floor(project.previewImages.length / 2) - 1} more
												before/after sets
											</p>
										)}
									</button>
									<Button
										type="button"
										variant={
											savedIdeas.includes(project.id) ? "secondary" : "outline"
										}
										size="sm"
										className="mt-3"
										disabled={toggleInFlight}
										onClick={(event) => {
											event.stopPropagation();
											void onToggleSaveIdea(project.id);
										}}
									>
										{savedIdeas.includes(project.id) ? "Saved" : "Save Idea"}
									</Button>
								</div>
							))}
						</div>
					</>
				)}
			</section>
		</div>
	);
}
